import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Heart, MessageSquare, UserPlus, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const iconMap = {
  like: Heart,
  comment: MessageSquare,
  connection_request: UserPlus,
  connection_accepted: Check,
  message: MessageSquare,
};

const colorMap = {
  like: "text-[#F85037]",
  comment: "text-[#074572]",
  connection_request: "text-[#074572]",
  connection_accepted: "text-green-600",
  message: "text-[#074572]",
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifications(data);
      } catch { toast.error("Failed to load notifications"); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch { toast.error("Failed"); }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-6" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Notifications</h1>
          <p className="text-sm text-zinc-500 mt-1">{unreadCount} unread notification{unreadCount !== 1 ? "s" : ""}</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" className="border-[#E6E6E6] text-[#074572] gap-2" onClick={markAllRead} data-testid="mark-all-read-btn">
            <CheckCheck size={14} /> Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-center py-12 text-zinc-400">Loading...</p>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <Bell size={40} className="mx-auto mb-3 opacity-50" />
          <p className="font-medium">No notifications</p>
          <p className="text-sm mt-1">You're all caught up!</p>
        </div>
      ) : (
        <div className="space-y-1">
          {notifications.map(n => {
            const Icon = iconMap[n.type] || Bell;
            const color = colorMap[n.type] || "text-zinc-500";
            return (
              <div
                key={n.id}
                className={`flex items-start gap-3 p-4 rounded-md transition-colors ${n.read ? "bg-white" : "bg-[#074572]/[0.02] border-l-2 border-[#074572]"}`}
                data-testid={`notification-${n.id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.read ? "bg-zinc-50" : "bg-[#074572]/5"}`}>
                  <Icon size={16} className={color} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${n.read ? "text-zinc-500" : "text-zinc-800 font-medium"}`}>{n.content}</p>
                  <p className="text-xs text-zinc-400 mt-1">{getTimeAgo(n.created_at)}</p>
                </div>
                {!n.read && <div className="notification-dot shrink-0 mt-2" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
